import { useState } from "react";
import { authApi, accountApi } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Settings as SettingsIcon, LogOut, User, Download, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ConnectivityHub from "@/components/uc/ConnectivityHub";
import SystemHealth from "@/components/uc/SystemHealth";
import PlatformControl from "@/components/uc/PlatformControl";

export default function SettingsDrawer() {
  const { user, patchUser, logout } = useAuth();
  const [name, setName] = useState(user?.command_center_name || "Command OS");
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [open, setOpen] = useState(false);

  const downloadData = async () => {
    try {
      const data = await accountApi.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "umbrella-command-data-export.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Full data export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const deleteAccount = async () => {
    const typed = window.prompt(
      "RIGHT TO BE FORGOTTEN — this permanently erases your account and ALL data (ventures, drafts, vault, payments, logs). Type your account email to confirm:");
    if (typed === null) return;
    try {
      await accountApi.deleteAccount(typed.trim());
      toast.success("Account erased. Farewell, Commander.");
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deletion failed");
    }
  };

  const save = async () => {
    try {
      await authApi.updateSettings({ command_center_name: name, phone_number: phone });
      patchUser({ command_center_name: name, phone_number: phone });
      toast.success("Command Center updated");
      setOpen(false);
    } catch { toast.error("Save failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="settings-trigger"
          className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-amber-500/50 transition-colors uc-focus"
          style={{ background: "var(--uc-surface-2)" }}
        >
          <SettingsIcon size={15} className="text-white/80"/>
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#0A0A0A] border-l border-white/10 text-white w-full sm:max-w-md p-6 overflow-y-auto uc-scroll" data-testid="settings-drawer">
        <SheetHeader>
          <div className="uc-chip mb-2 w-fit"><User size={11}/> COMMAND CENTER</div>
          <SheetTitle className="font-serif-display text-2xl text-white">Personalize</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          <div className="space-y-2">
            <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Command Center Name</Label>
            <Input
              data-testid="settings-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10 rounded-none bg-[#121212] border-white/10"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Phone (for SMS alerts)</Label>
            <Input
              data-testid="settings-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
              className="h-10 rounded-none bg-[#121212] border-white/10 font-mono-uc"
            />
            <p className="text-[10px] text-white/40">Requires backend Twilio credentials to be configured.</p>
          </div>
          <div className="space-y-2">
            <Label className="font-mono-uc text-[11px] uppercase tracking-widest text-white/60">Account</Label>
            <div className="p-3 border border-white/10 bg-black text-sm text-white/70 font-mono-uc break-all">
              {user?.email}
            </div>
          </div>

          <Button data-testid="settings-save-btn" onClick={save} className="uc-btn-amber rounded-none h-11 w-full">Save Settings</Button>
          <Button
            data-testid="settings-logout-btn"
            onClick={() => { logout(); window.location.href = "/login"; }}
            variant="ghost"
            className="rounded-none h-11 w-full border border-white/10 text-white/70 hover:bg-white/5"
          >
            <LogOut size={14} className="mr-2"/> Sign Out
          </Button>

          <div className="pt-6 border-t border-white/10">
            <div className="uc-chip mb-2"><Download size={11} className="text-blue-400"/> DATA &amp; PRIVACY</div>
            <div className="space-y-2">
              <Button
                data-testid="download-data-btn"
                onClick={downloadData}
                variant="ghost"
                className="rounded-none h-10 w-full border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs font-mono-uc uppercase tracking-wider"
              >
                <Download size={13} className="mr-2"/> Download My Data
              </Button>
              <Button
                data-testid="delete-account-btn"
                onClick={deleteAccount}
                variant="ghost"
                className="rounded-none h-10 w-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-mono-uc uppercase tracking-wider"
              >
                <Trash2 size={13} className="mr-2"/> Delete My Account
              </Button>
            </div>
            <p className="text-[10px] text-white/40 mt-1">Right to be Forgotten: export everything as JSON, or permanently erase your account and every associated record.</p>
          </div>
          <PlatformControl />
          <div className="pt-6 border-t border-white/10">
            <ConnectivityHub />
          </div>
          <div className="pt-6 border-t border-white/10">
            <SystemHealth />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
